/**
 * 公開アプリ設定
 * 年度ごとに軽微変更しやすい表示設定の既定値と整形処理を提供します。
 */

/** 既定の開催名 */
const DEFAULT_FESTIVAL_NAME = '生駒祭';

/** 既定の英字フェス表記 */
const DEFAULT_FESTIVAL_LABEL_EN = 'IKOMA FESTIVAL';

/** 既定の交換場所表記 */
const DEFAULT_EXCHANGE_PLACE_LABEL = '交換窓口';

/** 既定の問い合わせ先表記 */
const DEFAULT_SUPPORT_CONTACT_LABEL = '運営スタッフ';

/** 既定の来場者ユーザ名プレースホルダー */
const DEFAULT_VISITOR_USERNAME_PLACEHOLDER = '例: ikoma';

/** 既定のチュートリアル見出し */
const DEFAULT_TUTORIAL_WELCOME_TITLE = '生駒祭スタンプラリーへようこそ！';

/** 既定のチュートリアル本文 */
const DEFAULT_TUTORIAL_WELCOME_BODY =
  '5つの島の企画に参加してpt（ポイント）を集め、ランクアップ！集めたptで豪華な景品と交換できます。';

/** 来場者ホームで表示を切り替えられる機能キー。 */
export const HOME_FEATURE_VISIBILITY_KEYS = [
  'notices',
  'tutorial',
  'votingResults',
  'voting',
  'stallRanking',
  'ranking',
  'group',
  'campusMap',
  'surveysPreparing',
  'rewardsPreparing',
  'eventsPreparing',
  'exchangeApplicationsPaused',
] as const;

/** 来場者ホーム機能の表示設定キー。 */
export type HomeFeatureVisibilityKey = (typeof HOME_FEATURE_VISIBILITY_KEYS)[number];

/** 来場者ホーム機能の表示設定。 */
export type PublicHomeFeatureVisibility = Record<HomeFeatureVisibilityKey, boolean>;

/** 来場者ホーム機能の表示設定の既定値。 */
export const DEFAULT_HOME_FEATURE_VISIBILITY: Readonly<PublicHomeFeatureVisibility> = Object.freeze({
  notices: true,
  tutorial: true,
  votingResults: true,
  voting: true,
  stallRanking: true,
  ranking: true,
  group: true,
  campusMap: true,
  surveysPreparing: false,
  rewardsPreparing: false,
  eventsPreparing: false,
  exchangeApplicationsPaused: false,
});

/** 公開する島スタンプ閾値 */
export type PublicIslandThreshold = {
  islandId: string;
  islandLabel: string;
  titleName: string;
  publishedEventCount: number;
  requiredCount: number;
  rewardPoints: number;
};

/** 公開する勇者ランク */
export type PublicHeroRank = {
  rankId: string;
  name: string;
  minPoints: number;
  imagePath: string | null;
  imageUrl: string | null;
};

/** 既定のランキング表示件数 */
export const DEFAULT_RANKING_DISPLAY_LIMIT = 100;

/** 既定の島スタンプ閾値 */
export const DEFAULT_ISLAND_THRESHOLDS: readonly PublicIslandThreshold[] =
  Object.freeze(
    [1, 2, 3, 4, 5].map((islandNumber) => Object.freeze({
      islandId: `island-${islandNumber}`,
      islandLabel: `第${islandNumber}島`,
      titleName: `第${islandNumber}島マスター`,
      publishedEventCount: 0,
      requiredCount: 3,
      rewardPoints: 30,
    })),
  );

/** 既定の勇者ランク */
export const DEFAULT_HERO_RANKS: readonly PublicHeroRank[] = Object.freeze([
  Object.freeze({
    rankId: '00000000-0000-4000-8000-000000000001',
    name: 'はじまりの旅人',
    minPoints: 0,
    imagePath: null,
    imageUrl: null,
  }),
  Object.freeze({
    rankId: '00000000-0000-4000-8000-000000000002',
    name: 'かけだし冒険者',
    minPoints: 30,
    imagePath: null,
    imageUrl: null,
  }),
  Object.freeze({
    rankId: '00000000-0000-4000-8000-000000000003',
    name: '疾風の剣士',
    minPoints: 80,
    imagePath: null,
    imageUrl: null,
  }),
  Object.freeze({
    rankId: '00000000-0000-4000-8000-000000000004',
    name: '颯の英雄',
    minPoints: 150,
    imagePath: null,
    imageUrl: null,
  }),
]);

/** 来場者向け公開アプリ設定 */
export type PublicAppSettings = {
  eventStartDate: string;
  eventEndDate: string;
  eventDefaultStartTime: string;
  eventDefaultEndTime: string;
  festivalName: string;
  festivalLabelEn: string;
  exchangePlaceLabel: string;
  supportContactLabel: string;
  visitorUsernamePlaceholder: string;
  tutorialWelcomeTitle: string;
  tutorialWelcomeBody: string;
  homeFeatureVisibility: PublicHomeFeatureVisibility;
  rankingDisplayLimit: number;
  islandThresholds: readonly PublicIslandThreshold[];
  heroRanks: readonly PublicHeroRank[];
};

/** 公開アプリ設定の既定値 */
export const DEFAULT_PUBLIC_APP_SETTINGS: Readonly<PublicAppSettings> = Object.freeze({
  eventStartDate: '',
  eventEndDate: '',
  eventDefaultStartTime: '09:00',
  eventDefaultEndTime: '18:00',
  festivalName: DEFAULT_FESTIVAL_NAME,
  festivalLabelEn: DEFAULT_FESTIVAL_LABEL_EN,
  exchangePlaceLabel: DEFAULT_EXCHANGE_PLACE_LABEL,
  supportContactLabel: DEFAULT_SUPPORT_CONTACT_LABEL,
  visitorUsernamePlaceholder: DEFAULT_VISITOR_USERNAME_PLACEHOLDER,
  tutorialWelcomeTitle: DEFAULT_TUTORIAL_WELCOME_TITLE,
  tutorialWelcomeBody: DEFAULT_TUTORIAL_WELCOME_BODY,
  homeFeatureVisibility: DEFAULT_HOME_FEATURE_VISIBILITY,
  rankingDisplayLimit: DEFAULT_RANKING_DISPLAY_LIMIT,
  islandThresholds: DEFAULT_ISLAND_THRESHOLDS,
  heroRanks: DEFAULT_HERO_RANKS,
});

/**
 * 文字列設定を既定値付きで正規化する
 * @param {unknown} value - 入力値
 * @param {string} fallbackValue - 既定値
 * @returns {string} 正規化済み文字列
 */
export const normalizePublicSettingText = (value, fallbackValue) => {
  /** 正規化済み文字列 */
  const normalizedValue = String(value ?? '').trim();

  return normalizedValue || fallbackValue;
};

/**
 * 来場者向け表示文の旧EXP表記をpt表記へ正規化する
 * @param {unknown} value - 表示文
 * @returns {string} pt表記へ正規化した表示文
 */
export const normalizeVisitorPointDisplayText = (value) => {
  return String(value ?? '')
    .replace(/EXP[（(]経験値[）)]/giu, 'pt（ポイント）')
    .replace(/EXP/giu, 'pt');
};

/**
 * 来場者ホーム機能の表示設定を正規化する
 * @param {unknown} value - RPCから受け取った表示設定
 * @returns {PublicHomeFeatureVisibility} 正規化済み表示設定
 */
export const normalizeHomeFeatureVisibility = (
  value: unknown,
): PublicHomeFeatureVisibility => {
  /** オブジェクトとして扱える候補値 */
  let candidateValue: Record<string, unknown> | null = null;

  if (typeof value === 'string') {
    try {
      const parsedValue: unknown = JSON.parse(value);
      if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        candidateValue = parsedValue as Record<string, unknown>;
      }
    } catch (_error) {
      candidateValue = null;
    }
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    candidateValue = value as Record<string, unknown>;
  }

  return HOME_FEATURE_VISIBILITY_KEYS.reduce<PublicHomeFeatureVisibility>(
    (normalizedValue, featureKey) => {
      normalizedValue[featureKey] = typeof candidateValue?.[featureKey] === 'boolean'
        ? Boolean(candidateValue[featureKey])
        : DEFAULT_HOME_FEATURE_VISIBILITY[featureKey];
      return normalizedValue;
    },
    { ...DEFAULT_HOME_FEATURE_VISIBILITY },
  );
};

/**
 * JSONまたは配列を配列へ正規化する
 * @param {unknown} value - 入力値
 * @returns {unknown[]} 配列
 */
const normalizeJsonArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (_error) {
    return [];
  }
};

/**
 * 非負整数へ正規化する
 * @param {unknown} value - 入力値
 * @returns {number | null} 非負整数、不正時null
 */
const normalizeNonNegativeInteger = (value: unknown): number | null => {
  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return null;
  }

  return numericValue;
};

/**
 * ランキング表示件数を正規化する
 * @param {unknown} value - 入力値
 * @returns {number} 1〜1000の表示件数
 */
export const normalizeRankingDisplayLimit = (value: unknown): number => {
  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 1 || numericValue > 1000) {
    return DEFAULT_RANKING_DISPLAY_LIMIT;
  }

  return numericValue;
};

/**
 * 島スタンプ閾値を正規化する
 * @param {unknown} value - 入力値
 * @returns {PublicIslandThreshold[]} 5島の有効設定
 */
export const normalizeIslandThresholds = (
  value: unknown,
): PublicIslandThreshold[] => {
  const candidateValues = normalizeJsonArray(value);

  if (candidateValues.length !== DEFAULT_ISLAND_THRESHOLDS.length) {
    return DEFAULT_ISLAND_THRESHOLDS.map((threshold) => ({ ...threshold }));
  }

  const thresholdsByIslandId = new Map(
    candidateValues.map((candidateValue: any) => [
      String(candidateValue?.islandId ?? '').trim(),
      candidateValue,
    ]),
  );

  const normalizedThresholds = DEFAULT_ISLAND_THRESHOLDS.map(
    (defaultThreshold) => {
      const candidateValue: any = thresholdsByIslandId.get(
        defaultThreshold.islandId,
      );
      const publishedEventCount = normalizeNonNegativeInteger(
        candidateValue?.publishedEventCount,
      );
      const requiredCount = normalizeNonNegativeInteger(
        candidateValue?.requiredCount,
      );
      const rewardPoints = normalizeNonNegativeInteger(
        candidateValue?.rewardPoints,
      );

      if (
        !candidateValue
        || publishedEventCount === null
        || requiredCount === null
        || rewardPoints === null
        || requiredCount < 1
        || requiredCount > 10_000
        || rewardPoints < 1
        || rewardPoints > 10_000
      ) {
        return null;
      }

      return {
        islandId: defaultThreshold.islandId,
        islandLabel: normalizePublicSettingText(
          candidateValue.islandLabel,
          defaultThreshold.islandLabel,
        ),
        titleName: normalizePublicSettingText(
          candidateValue.titleName,
          defaultThreshold.titleName,
        ),
        publishedEventCount,
        requiredCount,
        rewardPoints,
      };
    },
  );

  if (normalizedThresholds.some((threshold) => threshold === null)) {
    return DEFAULT_ISLAND_THRESHOLDS.map((threshold) => ({ ...threshold }));
  }

  return normalizedThresholds as PublicIslandThreshold[];
};

/**
 * 勇者ランクを正規化する
 * @param {unknown} value - 入力値
 * @returns {PublicHeroRank[]} 最低pt昇順のランク
 */
export const normalizeHeroRanks = (value: unknown): PublicHeroRank[] => {
  const candidateValues = normalizeJsonArray(value);
  const seenRankIds = new Set<string>();
  let previousMinPoints = -1;

  if (candidateValues.length < 1 || candidateValues.length > 20) {
    return DEFAULT_HERO_RANKS.map((rank) => ({ ...rank }));
  }

  const normalizedRanks = candidateValues.map((candidateValue: any) => {
    const rankId = String(candidateValue?.rankId ?? '').trim();
    const name = String(candidateValue?.name ?? '').trim();
    const minPoints = normalizeNonNegativeInteger(candidateValue?.minPoints);
    const imagePath = String(candidateValue?.imagePath ?? '').trim() || null;
    const imageUrl = String(candidateValue?.imageUrl ?? '').trim() || null;

    if (
      !rankId
      || seenRankIds.has(rankId)
      || !name
      || minPoints === null
      || minPoints <= previousMinPoints
    ) {
      return null;
    }

    seenRankIds.add(rankId);
    previousMinPoints = minPoints;

    return {
      rankId,
      name,
      minPoints,
      imagePath,
      imageUrl,
    };
  });

  if (normalizedRanks.some((rank) => rank === null)) {
    return DEFAULT_HERO_RANKS.map((rank) => ({ ...rank }));
  }

  return normalizedRanks as PublicHeroRank[];
};

/**
 * RPC行を公開アプリ設定へ変換する
 * @param {any} row - RPC行
 * @returns {typeof DEFAULT_PUBLIC_APP_SETTINGS} 公開アプリ設定
 */
export const mapPublicAppSettingsRow = (row): PublicAppSettings => ({
  eventStartDate: normalizePublicSettingText(
    row?.event_start_date,
    DEFAULT_PUBLIC_APP_SETTINGS.eventStartDate,
  ),
  eventEndDate: normalizePublicSettingText(
    row?.event_end_date,
    DEFAULT_PUBLIC_APP_SETTINGS.eventEndDate,
  ),
  eventDefaultStartTime: normalizePublicSettingText(
    row?.event_default_start_time,
    DEFAULT_PUBLIC_APP_SETTINGS.eventDefaultStartTime,
  ),
  eventDefaultEndTime: normalizePublicSettingText(
    row?.event_default_end_time,
    DEFAULT_PUBLIC_APP_SETTINGS.eventDefaultEndTime,
  ),
  festivalName: normalizePublicSettingText(
    row?.festival_name,
    DEFAULT_PUBLIC_APP_SETTINGS.festivalName,
  ),
  festivalLabelEn: normalizePublicSettingText(
    row?.festival_label_en,
    DEFAULT_PUBLIC_APP_SETTINGS.festivalLabelEn,
  ),
  exchangePlaceLabel: normalizePublicSettingText(
    row?.exchange_place_label,
    DEFAULT_PUBLIC_APP_SETTINGS.exchangePlaceLabel,
  ),
  supportContactLabel: normalizePublicSettingText(
    row?.support_contact_label,
    DEFAULT_PUBLIC_APP_SETTINGS.supportContactLabel,
  ),
  visitorUsernamePlaceholder: normalizePublicSettingText(
    row?.visitor_username_placeholder,
    DEFAULT_PUBLIC_APP_SETTINGS.visitorUsernamePlaceholder,
  ),
  tutorialWelcomeTitle: normalizePublicSettingText(
    row?.tutorial_welcome_title,
    DEFAULT_PUBLIC_APP_SETTINGS.tutorialWelcomeTitle,
  ),
  tutorialWelcomeBody: normalizeVisitorPointDisplayText(
    normalizePublicSettingText(
      row?.tutorial_welcome_body,
      DEFAULT_PUBLIC_APP_SETTINGS.tutorialWelcomeBody,
    ),
  ),
  homeFeatureVisibility: normalizeHomeFeatureVisibility(row?.home_feature_visibility),
  rankingDisplayLimit: normalizeRankingDisplayLimit(
    row?.ranking_display_limit,
  ),
  islandThresholds: normalizeIslandThresholds(row?.island_thresholds),
  heroRanks: normalizeHeroRanks(row?.hero_ranks),
});
